import { isBillingFinancialRoute } from '@/config/axios.config';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useCompanionOnboardingStatus } from '@/hooks/use-companion-onboarding-status';
import { Alert } from '@/components/ui/alert';
import { Box, Button, Stack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Returns whether the given pathname is the companion wizard itself.
 * Used to prevent a redirect loop when the gate is active.
 */
const isWizardRoute = (pathname: string): boolean => pathname.startsWith('/onboarding/companion');

/** Brand Space has its own onboarding and must remain independent from Brand DNA. */
const bypassesCompanionGate = (pathname: string): boolean =>
  pathname === '/brand-space' ||
  pathname.startsWith('/brand-space/') ||
  isBillingFinancialRoute(pathname);

/**
 * Builds the redirect target for brands without DNA.
 * - Adds forced=true to signal the wizard it was entered involuntarily.
 * - Adds next=<encodedPath> only when the brand was trying to reach a
 *   non-dashboard route (the wizard does not restore a redirect to /dashboard
 *   because after completion the brand lands there anyway).
 */
const buildRedirectTo = (pathname: string, search: string): string => {
  const isDashboard = pathname === '/dashboard';
  const params = new URLSearchParams();
  params.set('forced', 'true');
  if (!isDashboard) {
    // URLSearchParams encodes the value itself — no encodeURIComponent here,
    // or the consumer would have to double-decode.
    params.set('next', pathname + search);
  }
  return `/onboarding/companion?${params.toString()}`;
};

interface CompanionGateProps {
  children: ReactNode;
}

/**
 * Enforces that brands without Brand DNA cannot access the main dashboard.
 *
 * Insert this between ProtectedRoute and Layout in MainProtectedLayout.
 * The wizard route (/onboarding/companion) is declared OUTSIDE the gated
 * layout in Router.tsx — no loop is possible.
 *
 * Rendering contract:
 *   - Brand Space route    → children (its own onboarding, independent of DNA)
 *   - failed brand read    → shared error alert and explicit retry
 *   - isResolving          → neutral full-viewport blank (no sidebar flash)
 *   - !isEnabled           → children (flag off, no gate)
 *   - isCompleted          → children
 *   - !hasDna (not wizard) → hard redirect to wizard with forced=true
 *   - otherwise            → children
 */
export const CompanionGate: React.FC<CompanionGateProps> = ({ children }) => {
  const { t } = useTranslation();
  const { data: brand, error, refetch, isFetching } = useCurrentBrandQuery();
  const { isEnabled, isResolving, hasDna, isCompleted } = useCompanionOnboardingStatus(brand?.id);
  const location = useLocation();

  // One component and one render path for every route, Brand Space included:
  // routing the bypass through a different element made React tear the whole
  // layout down and rebuild it on each move in or out of Brand Space, and the
  // rebuilt layout slid its content in from the left edge.
  if (bypassesCompanionGate(location.pathname)) {
    return <>{children}</>;
  }

  if (!brand?.id && error) {
    return (
      <Stack maxW="480px" mx="auto" px={5} py={10}>
        <Alert role="alert" status="error" title={t('common.errors.unexpected')} />
        <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </Stack>
    );
  }

  // Brand not resolved yet or DNA query still in flight: hold rendering.
  // Never render children here — that would flash the sidebar before the
  // redirect fires for brands without DNA.
  if (!brand?.id || isResolving) {
    return <Box minH="100dvh" bg="bg.subtle" />;
  }

  // Feature flag off: gate is transparent.
  if (!isEnabled) {
    return <>{children}</>;
  }

  // Wizard completed: gate is transparent.
  if (isCompleted) {
    return <>{children}</>;
  }

  // Brand has DNA: gate is transparent.
  if (hasDna) {
    return <>{children}</>;
  }

  // No DNA and not already on the wizard: redirect.
  if (!isWizardRoute(location.pathname)) {
    return <Navigate to={buildRedirectTo(location.pathname, location.search)} replace />;
  }

  // On the wizard route already (should not happen via this layout, but guard).
  return <>{children}</>;
};
